import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { loadSavedForm, saveForm } from './formUtils';
import NamesStep from './company/NamesStep';
import AddressStep from './company/AddressStep';
import DirectorsStep, { emptyDirector } from './company/DirectorsStep';
import ShareholdersStep, { emptyShareholder } from './company/ShareholdersStep';
import ReviewStep from './company/ReviewStep';
import { useCompanyRegistrationValidation } from './company/validation';

const DEFAULTS = {
  proposedName1: '',
  proposedName2: '',
  proposedName3: '',
  businessActivity: '',
  physicalAddress: '',
  postalAddress: '',
  sameAsPhysical: false,
  directors: [emptyDirector()],
  shareholders: [emptyShareholder()],
  financialYearEnd: '',
  publicOfficer: '',
  preferredContactMethod: 'email',
};

const steps = ['Company Names', 'Address', 'Directors', 'Shareholders', 'Review'];

const CompanyRegistrationForm = ({ serviceId, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [autoSaved, setAutoSaved] = useState(false);
  const validateStep = useCompanyRegistrationValidation();

  useEffect(() => {
    saveForm(serviceId, formData);
    setAutoSaved(true);
    const t = setTimeout(() => setAutoSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, formData]);

  const set = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const updateArr = (key, index, field, value) =>
    setFormData((p) => ({
      ...p,
      [key]: p[key].map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    }));

  const addRow = (key, make) => setFormData((p) => ({ ...p, [key]: [...p[key], make()] }));
  const removeRow = (key, idx) =>
    setFormData((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));

  const next = () => {
    if (!validateStep(currentStep, formData)) return;
    setCurrentStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setCurrentStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validateStep(currentStep, formData)) return;
    onComplete(formData);
  };

  const stepProps = { formData, set, updateArr, addRow, removeRow };

  const stepComponentByIndex = {
    1: <NamesStep {...stepProps} />,
    2: <AddressStep {...stepProps} />,
    3: <DirectorsStep {...stepProps} />,
    4: <ShareholdersStep {...stepProps} />,
    5: <ReviewStep {...stepProps} />,
  };

  return (
    <FormShell
      title="Company Registration Application"
      stepLabels={steps}
      currentStep={currentStep}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={autoSaved}
    >
      {stepComponentByIndex[currentStep] || null}
    </FormShell>
  );
};

export default CompanyRegistrationForm;
